import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';
import { useBookSymbols } from '../hooks/useBookSymbols';
import { useQuote } from '../contexts/QuoteContext';

const formatIv = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const formatNum = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US');
};

const formatRatio = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}x`;
};

const OptionsOverview = ({ onSymbolSelect }) => {
  const { symbol: quoteSymbol, selectSymbol } = useQuote();
  const { symbols: bookSymbols, loading: bookLoading } = useBookSymbols();
  const [symbol, setSymbol] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const choices = useMemo(() => {
    const set = new Set(bookSymbols);
    if (quoteSymbol) set.add(String(quoteSymbol).toUpperCase());
    if (symbol) set.add(String(symbol).toUpperCase());
    return [...set].sort();
  }, [bookSymbols, quoteSymbol, symbol]);

  useEffect(() => {
    if (quoteSymbol) {
      setSymbol(String(quoteSymbol).toUpperCase());
      return;
    }
    if (!symbol && bookSymbols[0]) setSymbol(bookSymbols[0]);
  }, [quoteSymbol, bookSymbols, symbol]);

  const load = async () => {
    if (!symbol) return;
    try {
      setLoading(true);
      setError('');
      const response = await marketDataApi.getOptionsOverview(symbol);
      setData(response);
      if (response.unavailable) {
        setError('Options feed temporarily unavailable (upstream timeout)');
      } else if (response.available === false) {
        setError(response.message || 'Options data unavailable on this Alpaca plan');
      }
    } catch (err) {
      console.error('Options overview error:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to load options';
      setError(msg);
      setData(err.response?.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const open = (sym) => {
    if (onSymbolSelect) onSymbolSelect(sym);
    else selectSymbol(sym);
  };

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faLayerGroup} className="text-xs text-muted" />
            <span className="lt-label">Options overview</span>
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            Nearest expiry · ATM IV · unusual volume
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="h-8 px-2 text-[11px] lt-input max-w-[96px]"
            disabled={bookLoading && !choices.length}
          >
            {!choices.length && <option value="">—</option>}
            {choices.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading || !symbol}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRotate} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div
          className={`mx-3 mt-3 px-2.5 py-2 rounded-md text-xs border ${
            /timeout|temporarily|unavailable/i.test(error)
              ? 'bg-elevated text-muted border-hairline'
              : 'bg-[var(--lt-bear-dim)] text-bear border-[rgba(240,113,103,0.28)]'
          }`}
        >
          {error}
        </div>
      )}

      <div className="px-3 py-3 space-y-3">
        {!symbol ? (
          <div className="text-xs text-muted text-center py-4">
            Select a symbol from your book or open a quote.
          </div>
        ) : loading && !data ? (
          <div className="text-xs text-muted text-center py-4">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading chain...
          </div>
        ) : data?.available === false ? (
          <div className="text-xs text-muted text-center py-2">
            Options entitlement may be required. Nearest listed expiry:{' '}
            <span className="num text-ink">{data.expiry || '—'}</span>
          </div>
        ) : data ? (
          <>
            <button
              type="button"
              onClick={() => open(data.symbol)}
              className="w-full text-left rounded-md border border-hairline bg-canvas px-3 py-2 hover:bg-elevated"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="lt-label mb-0.5">ATM IV</div>
                  <div className="num text-lg font-semibold text-ink">{formatIv(data.atm?.iv)}</div>
                </div>
                <div className="text-right">
                  <div className="lt-label mb-0.5">Expiry</div>
                  <div className="num text-xs text-ink">{data.expiry || '—'}</div>
                  <div className="text-[10px] text-muted mt-0.5">
                    Strike {data.atm?.strike != null ? Number(data.atm.strike).toFixed(2) : '—'}
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <div className="text-muted">Call IV</div>
                  <div className="num text-ink">{formatIv(data.atm?.call?.iv)}</div>
                </div>
                <div>
                  <div className="text-muted">Put IV</div>
                  <div className="num text-ink">{formatIv(data.atm?.put?.iv)}</div>
                </div>
              </div>
            </button>

            <div>
              <div className="lt-label mb-1.5">Unusual volume</div>
              {!data.unusual?.length ? (
                <div className="text-[11px] text-muted">No volume leaders for this expiry</div>
              ) : (
                <div className="divide-y divide-hairline rounded-md border border-hairline overflow-hidden">
                  {data.unusual.slice(0, 5).map((row) => (
                    <div key={row.contract} className="px-2.5 py-1.5 bg-canvas flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="num text-[11px] font-semibold text-ink truncate">
                          {row.type === 'call' ? 'C' : row.type === 'put' ? 'P' : '—'}{' '}
                          {row.strike != null ? Number(row.strike).toFixed(0) : '—'}
                        </div>
                        <div className="text-[10px] text-muted truncate">{row.contract}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="num text-[11px] text-ink">{formatNum(row.volume)}</div>
                        <div className="text-[10px] text-muted">
                          {row.volumeRatio != null ? formatRatio(row.volumeRatio) : 'vol'} · IV {formatIv(row.iv)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-xs text-muted text-center py-4">No options data</div>
        )}
      </div>
    </div>
  );
};

export default OptionsOverview;
